import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { Camera } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { formatValue } from './ProductSummary';

export function FullSpecs({ entries }) {
  const { t } = useLanguage();
  if (entries.length === 0) return null;
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron-50/30" data-testid="full-specs">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-display text-2xl font-bold text-earth-dark mb-8">{t.productDetail.fullSpecs}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {entries.map(([key, value]) => (
            <div key={key} className="bg-white p-5 rounded-lg border border-saffron-100/50">
              <p className="text-[10px] uppercase tracking-wider text-saffron font-sans font-semibold mb-1">{key.replace(/_/g, ' ')}</p>
              <p className="text-sm text-earth-dark font-sans font-medium">{formatValue(value, ' | ')}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OtherProductCard({ product }) {
  return (
    <Link
      to={`/products/${product.id}`}
      className="group bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover transition-all border border-saffron-100/50"
      data-testid={`other-${product.id}`}
    >
      {product.image ? (
        <div className="aspect-[4/3] overflow-hidden">
          <img src={product.image} alt={`${product.name} - Indian spice exporter`} width={800} height={600} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center">
          <Camera size={28} className="text-earth/20" />
        </div>
      )}
      <div className="p-4">
        <h3 className="font-display text-base font-bold text-earth-dark group-hover:text-saffron transition-colors">{product.name}</h3>
        <p className="text-xs text-earth/50 font-sans mt-0.5">{product.tagline}</p>
      </div>
    </Link>
  );
}

export function OtherProducts({ products }) {
  const { t } = useLanguage();
  if (products.length === 0) return null;
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="other-products">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-display text-2xl font-bold text-earth-dark mb-8">{t.productDetail.otherProducts}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          {products.map(p => <OtherProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}
